import React from "react"

import { RouteComponentProps } from "@reach/router"
import { Form, Formik } from "formik"
import { graphql, useStaticQuery } from "gatsby"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import { navigate } from "gatsby-plugin-intl"
import { useDatabaseObjectData } from "reactfire"

import { registerInvoice } from "../api"
import { groupsDb } from "../firebase"
import { useTranslations } from "../utils/intl"

import { FormikField } from "./form"
import {
  Alert,
  ContentWrapper,
  H1,
  H3,
  Loader,
  LoadingButton,
  P,
  shevy,
  SplitImage,
  Stack,
  theme,
} from "."

interface FieldDef {
  id: FormKeys
  label: [string, string]
  placeholder: [string, string]
  type?: "text" | "email" | "textarea"
  required?: true
}

type FormKeys =
  | "org_name"
  | "invoice_person"
  | "invoice_adress"
  | "invoice_city"
  | "invoice_department"
  | "invoice_box"
  | "invoice_postal"
  | "invoice_email"
  | "invoice_gln"
  | "invoice_reference"
  | "notes"

const form: FieldDef[] = [
  {
    id: "org_name",
    label: ["Name of the school", "Skolans namn"],
    placeholder: ["School name", "Namn på fakturan"],
    required: true,
  },
  {
    id: "invoice_person",
    label: ["Contact Name", "Kontaktperson"],
    placeholder: ["John Doe", "Anders Andersson"],
    required: true,
  },
  {
    id: "invoice_email",
    label: ["E-mail for contact", "E-post för kontakt"],
    placeholder: ["email@example.com", "anders@andersson.se"],
    type: "email",
    required: true,
  },
  {
    id: "invoice_adress",
    label: ["Address", "Adress"],
    placeholder: ["Street address", "Gatuadress"],
    required: true,
  },
  {
    id: "invoice_city",
    label: ["City", "Ort"],
    placeholder: ["City", "Ort"],
    required: true,
  },
  {
    id: "invoice_department",
    label: ["Department", "Avdelning"],
    placeholder: ["Department", "Avdelning"],
  },
  {
    id: "invoice_box",
    label: ["Post-office box", "Box"],
    placeholder: ["Post-office box", "Box"],
  },
  {
    id: "invoice_postal",
    label: ["Zip code", "Postnummer"],
    placeholder: ["Zip code", "XXX XX"],
    required: true,
  },
  {
    id: "invoice_gln",
    label: ["GLN", "GLN"],
    placeholder: ["736XXXXXXXXXY", "736XXXXXXXXXY"],
  },
  {
    id: "invoice_reference",
    label: ["Reference on invoice", "Referens på faktura"],
    placeholder: ["Reference on invoice", "Referens på faktura"],
  },
  {
    id: "notes",
    label: ["Optional note", "Valfri anteckning"],
    placeholder: ["Optional note", "Valfri anteckning"],
    type: "textarea",
  },
]

function toInitial(form: FieldDef[]): Record<string, string> {
  return form.reduce((acc, curr) => ({ ...acc, [curr.id]: "" }), {})
}

const GroupInvoiceForm: React.FC<{ groupId: string }> = ({
  groupId,
  ...props
}) => {
  const { it } = useTranslations()
  const group = useDatabaseObjectData<{
    name: string
    payment?: { until: number }
  }>(groupsDb.ref("groups").child(groupId)).data
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<null | string>(null)

  const groupPaid = group.payment && group.payment.until > Date.now()

  return (
    <div {...props}>
      <H1>{it(["Create invoice", "Skapa faktura"])}</H1>
      {groupPaid && (
        <P
          css={{
            fontWeight: 600,
            fontStyle: "italic",
            color: theme.colors.green,
          }}
        >
          {it(["This group is already paid", "Denna grupp är redan betald"])}
        </P>
      )}
      {it([
        <P>
          Give access to Runbit for all members in the group{" "}
          <span css={{ fontStyle: "italic" }}>{group.name}</span>.<br />
          <br />
          For 1200 SEK (ex. VAT) every member in the group will have access to
          Runbit for a year (365 days).
        </P>,
        <P>
          Ge fri åtkomst till Runbit för alla i gruppen{" "}
          <span css={{ fontStyle: "italic" }}>{group.name}</span>.<br />
          <br />
          För 1200 kr (ex.moms) får alla i gruppen full åtkomst till Runbit i
          ett år (365 dagar).
        </P>,
      ])}

      <InvoiceForm
        loading={loading}
        error={error}
        onSubmit={values => {
          console.log(values)
          setLoading(true)
          registerInvoice(groupId, values)
            .then(res => {
              if (res.status === 400) {
                if (typeof res.data === "string") {
                  setError(res.data)
                } else {
                  setError("Okänt fel")
                  throw new Error("Unknown error " + JSON.stringify(res.data))
                }
              } else {
                setLoading(false)
                navigate(`/group/${groupId}/complete`)
              }
            })
            .catch(e => {
              setLoading(false)
              setError("Okänt fel")
              throw e
            })
        }}
      />
    </div>
  )
}

export const InvoiceForm: React.FC<{
  onSubmit: (values: Record<string, string>) => void
  loading: boolean
  error: null | string
  prefill?: Record<string, string>
}> = ({ onSubmit, loading, error, prefill, ...props }) => {
  const { it } = useTranslations()

  return (
    <div {...props}>
      <H3>{it(["Invoice details", "Fakturerings-uppgifter"])}</H3>
      <Formik
        initialValues={{ ...toInitial(form), ...prefill }}
        onSubmit={onSubmit}
      >
        <Form>
          <Stack spacing={shevy.baseSpacing(1)}>
            {form.map(({ id, label, placeholder, required, type }) => (
              <FormikField
                key={id}
                label={it(label)}
                placeholder={it(placeholder)}
                name={id}
                required={required}
                type={type}
                as={type == "textarea" ? "textarea" : undefined}
              />
            ))}

            {error && (
              <Alert css={{ marginTop: shevy.baseSpacing() }}>
                <p>{error}</p>
                {it([
                  <p>
                    If you need help, contact us at{" "}
                    <a href="mailto:anders@runbit.se">anders@runbit.se</a>
                  </p>,
                  <p>
                    Om ni behöver hjälp kan ni kontakta oss på{" "}
                    <a href="mailto:anders@runbit.se">anders@runbit.se</a>
                  </p>,
                ])}
              </Alert>
            )}
            <div
              css={{
                display: "flex",
                flexDirection: "row-reverse",
                marginTop: shevy.baseSpacing(),
              }}
            >
              <LoadingButton
                type="submit"
                disabled={loading}
                css={{ color: theme.colors.white }}
                variant="filled"
                color={theme.colors.green}
              >
                {it(["Send", "Skicka"])}
              </LoadingButton>
            </div>
          </Stack>
        </Form>
      </Formik>
    </div>
  )
}

/* eslint-disable @typescript-eslint/no-non-null-assertion */

export const InvoiceContent: React.FC<
  RouteComponentProps<{
    groupId: string
  }>
> = ({ groupId, ...props }) => {
  return (
    <Layout>
      <React.Suspense fallback={<Loader />}>
        <GroupInvoiceForm groupId={groupId!} {...props} />
      </React.Suspense>
    </Layout>
  )
}

export const InvoiceComplete: React.FC<
  RouteComponentProps<{
    groupId: string
  }>
> = props => {
  const { it } = useTranslations()

  return (
    <Layout {...props}>
      <H1>{it(["Invoice registered", "Faktura registrerad"])}</H1>
      <P>
        {it([
          "We will process your request and send the invoice soon.",
          "Vi behandlar din faktura och skickar iväg den snart.",
        ])}
      </P>
      <P>
        {it([
          "The group is now unlocked so you can get started right away!",
          "Gruppen är upplåst nu, så det är bara att köra på!",
        ])}
      </P>
    </Layout>
  )
}

const Layout: React.FC = ({ ...props }) => {
  const {
    file: { childImageSharp: image },
  } = useStaticQuery(graphql`
    query {
      file(relativePath: { eq: "girl-boy-phone-hand.jpg" }) {
        childImageSharp {
          gatsbyImageData(
            sizes: "max(50vw, 100vh)"
            quality: 80
            formats: [AUTO, JPG]
            placeholder: TRACED_SVG
            breakpoints: [960, 1024, 1920, 2100]
          )
        }
      }
    }
  `)
  return (
    <SplitImage
      image={
        <GatsbyImage alt="Looking at runbit app" image={getImage(image)!} />
      }
      {...props}
    >
      <ContentWrapper
        css={{
          maxWidth: 700,
          marginTop: shevy.baseSpacing(2),
          marginBottom: shevy.baseSpacing(2),
        }}
      >
        {props.children}
      </ContentWrapper>
    </SplitImage>
  )
}
